import styled from '@emotion/styled';
import { Button, Link } from '@kep-ui-kit/ui-components';
import { StyledContentContainer } from './styled-components';

const V2_GRADIENT =
  'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgb(255, 255, 255) 100%), linear-gradient(269.69deg, rgb(0, 110, 250) 0%, rgb(255, 157, 255) 49.5%, rgb(91, 198, 134) 100%)';

export const StyledV2PageWrapper = styled.div({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundImage: V2_GRADIENT,
});

export const StyledV2LogoRow = styled.div({
  width: '100%',
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  flexShrink: 0,
});

export const StyledV2PageBackground = styled(StyledContentContainer)(({ theme }: { theme?: any }) => ({
  flex: 1,
  padding: '48px 24px',
}));

export const StyledV2GlassCard = styled.div<{ maxWidth?: string }>(
  ({ maxWidth = '480px' }: { theme?: any; maxWidth?: string }) => ({
    width: '100%',
    maxWidth,
    display: 'flex',
    flexDirection: 'column',
    gap: '48px',
    padding: '40px',
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(2px)',
    '@media (max-width: 600px)': {
      padding: '24px',
      gap: '32px',
    },
  })
);

export const StyledV2Input = styled.input<{ hasError?: boolean }>(({ theme, hasError }: { theme?: any; hasError?: boolean }) => ({
  backgroundColor: '#fff',
  border: `1px solid ${hasError ? theme.palette.danger.main : theme.palette.grey['300']}`,
  borderRadius: theme.shape.borderRadius,
  height: '40px',
  padding: theme.spacing(1),
  fontSize: '16px',
  lineHeight: '16px',
  color: theme.palette.text.primary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  '&:focus': {
    borderColor: hasError ? theme.palette.danger.main : theme.palette.primary.main,
  },
  '&::placeholder': {
    color: theme.palette.grey['300'],
  },
}));

export const StyledV2Link = styled(Link)({
  fontWeight: 700,
  cursor: 'pointer',
  textDecorationSkipInk: 'none',
});

export const StyledV2MailIllustration = styled.img({
  width: '300px',
  height: '300px',
  flexShrink: 0,
});

export const StyledV2Checkbox = styled.input(({ theme }: { theme?: any }) => ({
  width: '18px',
  height: '18px',
  border: `1px solid ${theme.palette.grey['300']}`,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  flexShrink: 0,
  accentColor: theme.palette.primary.main,
}));

export const StyledV2RequirementsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginTop: '8px',
});

export const StyledV2Requirement = styled.div(
  ({ hasError, touched }: { theme?: any; hasError?: boolean; touched?: boolean }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '8px',
    p: {
      fontSize: '16px',
      fontFamily: 'Lato, sans-serif',
      fontWeight: 400,
      lineHeight: '21px',
      margin: 0,
      color: touched && hasError ? '#E12437' : '#333',
    },
  })
);

export const StyledV2RequirementIcon = styled.img(
  ({ hasError, touched }: { theme?: any; hasError?: boolean; touched?: boolean }) => ({
    maxWidth: '8px',
    maxHeight: '8px',
    padding: '4px',
    backgroundColor: '#CCCCCC',
    borderRadius: '50%',
    ...(touched && hasError
      ? { padding: '3px', backgroundColor: '#E12437', maxWidth: '20px', maxHeight: '20px' }
      : touched && { backgroundColor: '#00A078', padding: '3px', maxWidth: '20px', maxHeight: '20px' }),
  })
);

// Button size XL which not exists in the current version of @kep-ui-kit/ui-components
export const StyledV2SubmitBtn = styled(Button)(({ theme }: { theme?: any }) => ({
  fontSize: '16px',
  height: '48px',
  padding: '16px',
  '&:disabled, &.Mui-disabled': {
    backgroundColor: theme.palette.grey['400'],
  },
}));

// using Button of @kep-ui-kit/ui-components with the custom styles to make it look like an icon button for the eye icon
export const StyledV2EyeButton = styled(Button)(({ theme }: { theme?: any }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.grey['300'],
  minWidth: 0,
  padding: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  '&:hover': {
    background: 'none',
  },
}));

// using Button of @kep-ui-kit/ui-components with custom styles to look like a link
// the Link component of @kep-ui-kit/ui-components is not accessible and does not have the hover effect
export const StyledV2LinkButton = styled(Button)<{ fontSize?: string }>(({ theme, fontSize }: { theme?: any; fontSize?: string }) => ({
  fontSize: fontSize ?? '14px',
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  alignSelf: 'center',
  '&&': {
    background: 'none',
  },
  '&:hover': { textDecoration: 'underline' },
}));
